import React, { useEffect, useRef, useState } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { getContentChoices, listContentObjects } from "../../../actions";
import { brand } from "../../../theme/businessFlowsTheme";

// The same rules the server applies (backend/services/content.js), so the
// form says what's wrong before anything is sent.
const TYPE_NAME = /^[\p{L}\p{N}][\p{L}\p{N} _&/().,'+-]{0,79}$/u;
const RELATIONSHIP = /^[A-Za-z][A-Za-z0-9_]{0,59}$/;
const ATTRIBUTE = /^[A-Za-z][A-Za-z0-9 _-]{0,59}$/;
const RESERVED_TYPES = ["bpmn", "svg", "png", "pdf", "mermaid", "diagram", "root", "viewtype", "viewrelationship", "viewfield", "viewsetting", "viewconfiglock", "paneltype", "panelattribute", "searchtype", "explorerstart", "contentarchiveentry"];
const DIAGRAM_LINKS = ["BPMN_OF", "SVG_OF", "DIAGRAM_OF"];
const FALLBACK_PROTECTED = ["GUID", "Type", "inTrial", "hasAssignment", "createdAt", "createdBy", "updatedAt", "updatedBy", "archivedAt", "archivedBy", "supersededBy"];

export const typeProblem = (value) => {
  const type = value.trim();
  if (!type) return "";
  if (!TYPE_NAME.test(type)) return "Letters, digits, spaces and - _ & / ( ) . , ' +; start with a letter or digit.";
  if (RESERVED_TYPES.includes(type.toLowerCase())) return `${type} is a name Business Flows uses itself.`;
  return "";
};

export const relationshipProblem = (value) => {
  const relationship = value.trim();
  if (!relationship) return "";
  if (!RELATIONSHIP.test(relationship)) return "Letters, digits and underscores, starting with a letter, e.g. PART_OF.";
  if (DIAGRAM_LINKS.includes(relationship.toUpperCase())) return `${relationship} links diagrams to what they show.`;
  return "";
};

export const attributeProblem = (key, protectedFields, others) => {
  const name = key.trim();
  if (!name) return "";
  if (!ATTRIBUTE.test(name)) return "Letters, digits, spaces, dashes and underscores; start with a letter.";
  if (name === "name" || name === "Description" || protectedFields.includes(name)) return `${name} is set for you.`;
  if (others.includes(name)) return "Already added.";
  return "";
};

const filter = createFilterOptions();
const popup = { paper: { className: "bfx", sx: { borderRadius: 3, border: `1px solid ${brand.line}`, mt: 0.5 } } };

// An autocomplete over what the database has, where a new name can be typed
// too: the list ends with "Create ... X" whenever X isn't there yet.
function NameField({ label, value, onChange, options, newLabel, problem, helper, autoFocus, inputProps }) {
  return (
    <Autocomplete
      freeSolo
      options={options}
      inputValue={value}
      value={null}
      componentsProps={popup}
      onInputChange={(event, next, reason) => {
        if (reason !== "reset") onChange(next || "");
      }}
      onChange={(event, next) => {
        if (next && typeof next === "object") onChange(next.value);
        else if (typeof next === "string") onChange(next);
      }}
      filterOptions={(list, params) => {
        const filtered = filter(list, params);
        const typed = params.inputValue.trim();
        if (typed && !list.some((option) => option.value === typed)) filtered.push({ value: typed, isNew: true, label: newLabel(typed) });
        return filtered;
      }}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={`${option.isNew ? "new-" : ""}${option.value}`}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
            {option.isNew && <AddRoundedIcon fontSize="small" sx={{ color: brand.signalDeep }} />}
            <Typography variant="body2" sx={{ flex: 1, fontWeight: option.isNew ? 650 : 500 }}>
              {option.label}
            </Typography>
            {option.hint && (
              <Typography variant="caption" color="text.secondary">
                {option.hint}
              </Typography>
            )}
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          autoFocus={autoFocus}
          error={Boolean(problem)}
          helperText={problem || helper}
          inputProps={{ ...params.inputProps, ...inputProps }}
        />
      )}
    />
  );
}

// Creates an object of any type under any object. The type and the
// relationship are picked from what the database already has, or typed new;
// the relationship defaults to what the database uses between the two types.
export default function CreateObjectDialog({ open, database, parent: initialParent, protectedFields, onClose, onCreate }) {
  const [parent, setParent] = useState(null);
  const [parentQuery, setParentQuery] = useState("");
  const [parentOptions, setParentOptions] = useState([]);
  const [choices, setChoices] = useState({ types: [], relationships: [] });
  const [type, setType] = useState("");
  const [relationship, setRelationship] = useState("");
  const [direction, setDirection] = useState("toParent");
  const [suggestion, setSuggestion] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [attributes, setAttributes] = useState([]);
  const [busy, setBusy] = useState(false);
  // Once the editor picks a relationship or direction, suggestions stop overwriting it.
  const touched = useRef(false);
  const protectedList = protectedFields && protectedFields.length ? protectedFields : FALLBACK_PROTECTED;

  useEffect(() => {
    if (!open) return;
    touched.current = false;
    setParent(initialParent ? { guid: initialParent.GUID, name: initialParent.name, type: initialParent.Type } : null);
    setParentQuery("");
    setType("");
    setRelationship("");
    setDirection("toParent");
    setSuggestion(null);
    setName("");
    setDescription("");
    setAttributes([]);
    getContentChoices(database).then((res) => {
      if (res?.success) setChoices({ types: res.content.types || [], relationships: res.content.relationships || [] });
    });
  }, [open, database, initialParent]);

  // Search any object to put the new one under.
  useEffect(() => {
    if (!open || !parentQuery.trim()) return undefined;
    const timer = setTimeout(async () => {
      const res = await listContentObjects(database, { query: parentQuery.trim(), limit: 20 });
      setParentOptions(res?.success ? res.content || [] : []);
    }, 300);
    return () => clearTimeout(timer);
  }, [open, database, parentQuery]);

  // What the database uses between the parent's type and this one.
  const trimmedType = type.trim();
  const parentGuid = parent?.guid;
  useEffect(() => {
    if (!open || !trimmedType || typeProblem(trimmedType)) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      const res = await getContentChoices(database, { parentGuid, type: trimmedType });
      const next = res?.success ? res.content.suggestion : null;
      if (!active || !next) return;
      setSuggestion(next);
      if (!touched.current) {
        setRelationship(next.relationship);
        setDirection(next.direction || "toParent");
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, database, parentGuid, trimmedType]);

  if (!open) return null;

  const typeError = typeProblem(type);
  const relationshipError = relationshipProblem(relationship);
  const keys = attributes.map((a) => a.key.trim());
  const attributeErrors = attributes.map((a, index) => attributeProblem(a.key, protectedList, keys.filter((_, i) => i !== index)));
  const ready =
    parent &&
    trimmedType &&
    !typeError &&
    relationship.trim() &&
    !relationshipError &&
    name.trim() &&
    attributeErrors.every((problem) => !problem) &&
    attributes.every((a) => a.key.trim() || !a.value.trim());
  const knownType = choices.types.some((t) => t.type === trimmedType);
  const knownRelationship = choices.relationships.some((r) => r.type === relationship.trim());
  const link = relationship.trim() || "…";
  const child = name.trim() || "New object";

  const submit = async () => {
    setBusy(true);
    const payload = {
      parentGuid: parent.guid,
      type: trimmedType,
      name: name.trim(),
      description: description.trim(),
      relationship: relationship.trim(),
      direction,
      attributes: Object.fromEntries(attributes.filter((a) => a.key.trim()).map((a) => [a.key.trim(), a.value])),
    };
    await onCreate(payload);
    setBusy(false);
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ className: "bfx", sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Add an object</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Autocomplete
            options={parentOptions}
            value={parent}
            onChange={(event, next) => setParent(next)}
            onInputChange={(event, next, reason) => reason === "input" && setParentQuery(next)}
            filterOptions={(list) => list}
            componentsProps={popup}
            isOptionEqualToValue={(option, value) => option.guid === value.guid}
            getOptionLabel={(option) => option.name || ""}
            noOptionsText={parentQuery.trim() ? "Nothing found" : "Type to search any object"}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.guid}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.type}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Parent" helperText={parent ? `${parent.type || "Object"} · the new object is created under it` : "Search any object"} />
            )}
          />

          <NameField
            label="Type"
            value={type}
            onChange={setType}
            autoFocus
            options={choices.types.map((t) => ({ value: t.type, label: t.label || t.type, hint: t.objects ? `${t.objects}` : "" }))}
            newLabel={(typed) => `Create type “${typed}”`}
            problem={typeError}
            helper={trimmedType && !knownType ? "A new type: it exists once this object is created." : "Pick a type this database has, or type a new one."}
          />

          <Box>
            <NameField
              label="Relationship"
              value={relationship}
              onChange={(next) => {
                touched.current = true;
                setRelationship(next);
              }}
              options={choices.relationships.map((r) => ({ value: r.type, label: r.type, hint: r.count ? `${r.count}` : "" }))}
              newLabel={(typed) => `Use new relationship “${typed}”`}
              problem={relationshipError}
              helper={
                relationship.trim() && !knownRelationship
                  ? "A new relationship type."
                  : suggestion && relationship.trim() === suggestion.relationship
                  ? suggestion.source === "used"
                    ? "What this database already uses between these types."
                    : suggestion.source === "model"
                    ? "What this type is normally linked with."
                    : "No link between these types yet; PART_OF is a sensible start."
                  : "Pick one this database uses, or type a new one."
              }
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mt: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={direction}
                onChange={(event, next) => {
                  if (!next) return;
                  touched.current = true;
                  setDirection(next);
                }}
                aria-label="Direction"
              >
                <ToggleButton value="toParent" sx={{ textTransform: "none" }}>
                  New → parent
                </ToggleButton>
                <ToggleButton value="fromParent" sx={{ textTransform: "none" }}>
                  Parent → new
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }} data-testid="link-preview">
                {direction === "toParent" ? `${child} —${link}→ ${parent?.name || "parent"}` : `${parent?.name || "parent"} —${link}→ ${child}`}
              </Typography>
            </Stack>
          </Box>

          <TextField size="small" label="Name" required value={name} onChange={(e) => setName(e.target.value)} />
          <TextField size="small" label="Description" multiline rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />

          {attributes.map((attribute, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                label="Attribute"
                value={attribute.key}
                error={Boolean(attributeErrors[index])}
                helperText={attributeErrors[index] || " "}
                onChange={(e) => setAttributes((list) => list.map((a, i) => (i === index ? { ...a, key: e.target.value } : a)))}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Value"
                value={attribute.value}
                helperText=" "
                onChange={(e) => setAttributes((list) => list.map((a, i) => (i === index ? { ...a, value: e.target.value } : a)))}
                sx={{ flex: 1.4 }}
              />
              <IconButton aria-label="Remove attribute" onClick={() => setAttributes((list) => list.filter((_, i) => i !== index))} sx={{ mt: 0.5 }}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button startIcon={<AddRoundedIcon />} onClick={() => setAttributes((list) => [...list, { key: "", value: "" }])} sx={{ alignSelf: "flex-start" }}>
            Add attribute
          </Button>
          <Typography variant="caption" color="text.secondary">
            It inherits the parent's trial visibility, so trial users see it only if the parent is in the trial.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!ready || busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AddRoundedIcon />}
          onClick={submit}
        >
          Create object
        </Button>
      </DialogActions>
    </Dialog>
  );
}
